import React, { useState } from 'react';
import { Globe, ExternalLink, Star } from 'lucide-react';

export const ExampleWebsites: React.FC = () => {
  const [riskImageError, setRiskImageError] = useState(false);
  const [riskImageLoading, setRiskImageLoading] = useState(true);
  const [annuityImageError, setAnnuityImageError] = useState(false);
  const [annuityImageLoading, setAnnuityImageLoading] = useState(true);

  const handleRiskImageLoad = () => {
    setRiskImageLoading(false);
  };

  const handleRiskImageError = () => {
    setRiskImageError(true);
    setRiskImageLoading(false);
  };

  const handleAnnuityImageLoad = () => {
    setAnnuityImageLoading(false);
  };

  const handleAnnuityImageError = () => {
    setAnnuityImageError(true);
    setAnnuityImageLoading(false);
  };

  const FallbackThumbnail = ({ url, title }: { url: string; title: string }) => (
    <div className="w-full h-[280px] bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-lg flex items-center justify-center border border-white/10">
      <div className="text-center">
        <Globe className="w-16 h-16 text-white/60 mx-auto mb-3" />
        <p className="text-white/60 text-lg font-medium">{title}</p>
        <p className="text-white/40 text-sm mt-1">{url}</p>
      </div>
    </div>
  );

  const LoadingThumbnail = () => (
    <div className="w-full h-[280px] bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-lg flex items-center justify-center animate-pulse">
      <div className="text-center">
        <Globe className="w-12 h-12 text-white/40 mx-auto mb-2 animate-spin" />
        <p className="text-white/40 text-sm">Loading preview...</p>
      </div>
    </div>
  );

  return (
    <div className="mt-20">
      <div className="text-center mb-12">
        <div className="flex justify-center items-center mb-4">
          <Star className="text-yellow-400 mr-2" size={32} />
          <h3 className="text-3xl font-bold">Examples of Landing Pages We've Created</h3>
          <Star className="text-yellow-400 ml-2" size={32} />
        </div>
        <p className="text-xl text-gray-300">
          See the power of our AI-driven solutions in action
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Risk Number Website */}
        <div className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-white/20 transition-all duration-300 group">
          <div className="relative mb-6">
            {riskImageLoading && <LoadingThumbnail />}
            
            {!riskImageError ? (
              <img 
                src="https://api.microlink.io/?url=https%3A%2F%2Fwhatismyrisknumber.com&screenshot=true&embed=screenshot.url&viewport.width=1200&viewport.height=800&waitFor=2000&fullPage=false&type=png"
                alt="What Is My Risk Number website preview showing comprehensive financial risk assessment tools and interactive questionnaires"
                className={`w-full h-[280px] object-cover rounded-lg shadow-lg border border-white/10 transition-all duration-300 group-hover:shadow-xl ${riskImageLoading ? 'opacity-0 absolute' : 'opacity-100'}`}
                onLoad={handleRiskImageLoad}
                onError={handleRiskImageError}
                loading="lazy"
              />
            ) : (
              <FallbackThumbnail url="whatismyrisknumber.com" title="Risk Assessment Tool" />
            )}
            
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
              <div className="p-4 text-white">
                <ExternalLink className="w-5 h-5" />
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <h4 className="text-2xl font-bold text-white mb-2">Risk Assessment Platform</h4>
              <p className="text-yellow-400 font-medium">whatismyrisknumber.com</p>
            </div>
            
            <p className="text-gray-300 leading-relaxed">
              Interactive financial risk assessment tool that helps advisors understand their clients' risk tolerance through comprehensive questionnaires and AI-powered analysis.
            </p>
            
            <div className="flex flex-wrap gap-2">
              <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">Risk Analysis</span>
              <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm">Client Tools</span>
              <span className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm">Lead Generation</span>
            </div>
            
            <a 
              href="https://whatismyrisknumber.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center text-yellow-400 hover:text-yellow-300 transition-colors font-medium"
            >
              Visit Website
              <ExternalLink className="w-4 h-4 ml-2" />
            </a>
          </div>
        </div>

        {/* Annuity Analysis Website */}
        <div className="bg-gradient-to-br from-purple-600/20 to-pink-600/20 backdrop-blur-sm rounded-xl p-6 border border-white/10 hover:border-white/20 transition-all duration-300 group">
          <div className="relative mb-6">
            {annuityImageLoading && <LoadingThumbnail />}
            
            {!annuityImageError ? (
              <img 
                src="https://api.microlink.io/?url=https://analysis.associates&screenshot=true&embed=screenshot.url&viewport.width=1200&viewport.height=800"
                alt="Analysis Associates website preview showing comprehensive annuity comparison tools and financial planning calculators"
                className={`w-full h-[280px] object-cover rounded-lg shadow-lg border border-white/10 transition-all duration-300 group-hover:shadow-xl ${annuityImageLoading ? 'opacity-0 absolute' : 'opacity-100'}`}
                onLoad={handleAnnuityImageLoad}
                onError={handleAnnuityImageError}
                loading="lazy"
              />
            ) : (
              <FallbackThumbnail url="analysis.associates" title="Annuity Analysis Tool" />
            )}
            
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
              <div className="p-4 text-white">
                <ExternalLink className="w-5 h-5" />
              </div>
            </div>
          </div>
          
          <div className="space-y-4">
            <div>
              <h4 className="text-2xl font-bold text-white mb-2">Annuity Analysis Tool</h4>
              <p className="text-yellow-400 font-medium">analysis.associates</p>
            </div>
            
            <p className="text-gray-300 leading-relaxed">
              Sophisticated annuity comparison and analysis platform that empowers financial professionals with advanced calculators and client presentation tools.
            </p>
            
            <div className="flex flex-wrap gap-2">
              <span className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm">Annuity Tools</span>
              <span className="px-3 py-1 bg-pink-500/20 text-pink-300 rounded-full text-sm">Calculators</span>
              <span className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm">Client Reports</span>
            </div>
            
            <a 
              href="https://analysis.associates" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center text-yellow-400 hover:text-yellow-300 transition-colors font-medium"
            >
              Visit Website
              <ExternalLink className="w-4 h-4 ml-2" />
            </a>
          </div>
        </div>
      </div>

      <div className="text-center mt-12">
        <p className="text-lg text-gray-300 mb-6">
          Ready to see what we can create for your business?
        </p>
        <div className="flex justify-center items-center space-x-2 text-yellow-400">
          <Star className="w-5 h-5" />
          <span className="font-medium">Custom solutions tailored to your industry</span>
          <Star className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
};